import {
  Alert,
  Button,
  ButtonIcon,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import {
  type PodTools,
  type PodToolsContext,
  ActionIcon,
  StatusChip,
  waadooIcons,
} from '@internal/plugin-waadoo-core';
import { isShellPod } from './odooPods';
import { odooTranslationRef } from './translation';
import { hasShellDeployment } from './odooShell';
import { useOdooShell } from './useOdooShell';

/** The stop of the Odoo shell's row. */
const StopIcon = waadooIcons.stop;

/**
 * L'Odoo shell dans la carte des pods de la page d'une branche
 * (`PodTools`), déplacé de BranchPage.tsx à l'étape 9d, au même endroit de
 * ses hooks.
 *
 * THE ODOO SHELL (chart 0.9.8 and later): offered with the terminal, to
 * the same callers (`available`), when the Application ships its
 * Deployment (`hasShellDeployment`, on the page's last tree).
 */
export function useOdooPodTools(context: PodToolsContext): PodTools {
  const { available, tree, reloadPods } = context;
  const shellAvailable =
    available &&
    tree !== undefined &&
    hasShellDeployment(tree.nodes, tree.namespace);
  const shell = useOdooShell({
    envKey: context.envKey,
    project: context.project,
    namespace: context.namespace,
    appName: context.appName,
    pods: context.pods,
    reloadPods,
    openTerminal: context.openTerminal,
    closeTerminals: context.closeTerminals,
  });
  const { t } = useTranslationRef(odooTranslationRef);
  const stoppingHere = (pod: string) =>
    shell.phase === 'stopping' && shell.stoppingPod === pod;

  return {
    // Le suivi des pods ne relit pas pendant que le shell lit l'arbre
    // lui-même (`reloadPods`).
    busy: shell.phase !== 'idle',
    cardAction: shellAvailable && (
      <Button
        size="small"
        variant="secondary"
        iconStart={<ActionIcon name="odoo-shell" />}
        isPending={shell.phase === 'starting'}
        isDisabled={shell.phase !== 'idle'}
        onPress={shell.open}
      >
        {t('shell.open')}
      </Button>
    ),
    cardNotice: shellAvailable && shell.phase === 'starting' && (
      <Alert
        status="info"
        isPending
        title={t('shell.starting')}
        style={{ marginBottom: 'var(--bui-space-3)' }}
      />
    ),
    // « Arrêt… » from the stop until the pod is gone.
    rowStatus: pod =>
      stoppingHere(pod.name) ? (
        <StatusChip status="progressing" label={t('shell.stopping')} />
      ) : undefined,
    // The Odoo shell's pod: stopped by hand, or on its own after 30 min
    // without a session.
    rowActions: pod =>
      isShellPod(pod) &&
      shellAvailable && (
        <TooltipTrigger>
          <ButtonIcon
            size="small"
            variant="secondary"
            aria-label={t('shell.stopFor', {
              pod: pod.name,
            })}
            icon={
              <span className="waadoo-action-icon">
                <StopIcon aria-hidden focusable="false" />
              </span>
            }
            isPending={stoppingHere(pod.name)}
            isDisabled={shell.phase !== 'idle'}
            onPress={() => shell.stop(pod.name)}
          />
          <Tooltip>{t('shell.stopTooltip')}</Tooltip>
        </TooltipTrigger>
      ),
    terminalNotice: pod =>
      isShellPod(pod) ? (
        <Text variant="body-small" color="secondary">
          {t('shell.help')}
        </Text>
      ) : undefined,
    // The Odoo shell's pod stops on its own 30 min after its last session:
    // the list is read again so that a pod gone meanwhile leaves it.
    onTerminalClosed: pod => {
      if (isShellPod(pod)) {
        reloadPods().catch(() => undefined);
      }
    },
    // Le « Ouvrir un Odoo shell » de la carte des pods, dans le compte rendu
    // d'une branche prête : son terminal s'ouvre dans cette carte, sur
    // l'aperçu (`beforeOpen`).
    readyAction: beforeOpen =>
      shellAvailable ? (
        <Button
          variant="secondary"
          iconStart={<ActionIcon name="odoo-shell" />}
          isDisabled={shell.phase !== 'idle'}
          onPress={() => {
            beforeOpen();
            shell.open();
          }}
        >
          {t('report.shell')}
        </Button>
      ) : undefined,
  };
}
